/**
 * Format a source's page/section locator into display parts for the chat
 * Sources footer (a two-line entry: title, then a location line).
 *
 * The backend (`source_locator.finalize_locator`) sends a structured locator
 * (kind + numeric positions + section heading). We surface BOTH:
 *
 *   - `numeric`: the page/slide/sheet/line label parts (kindKey + count +
 *     positions). The kind word ("Page"/"Seite", ...) is localized in the
 *     component, so the label follows the user's UI language. Absent for
 *     reflowable DOCX/HTML which have no page number (loc_kind="section").
 *   - `section`: the chapter/section/subsection heading chain (e.g.
 *     "3 Setup > 3.2 OAuth"), verbatim from the document (its own language).
 *
 * The location line renders as e.g. "Page 2 · 3.1 Konditionssatz ZP01 (VK13)"
 * for a PDF, "3 Setup" alone for a Word doc, or "Page 2" alone when a chunk has
 * no heading. Returns null only when there is nothing at all to show.
 */

import { SourceLocator } from '../../api/chatTypes';

/** i18n key suffix under chat.sources.locator.kind for each numeric kind. */
const KIND_KEY: Record<string, string> = {
  page: 'page',
  slide: 'slide',
  sheet: 'sheet',
  line: 'line',
  // Region locators (images/scans with bbox provenance) carry page numbers;
  // they label like pages, the bboxes only affect the viewer overlay.
  region: 'page',
};

/** Format seconds as m:ss / h:mm:ss for transcript timestamp locators. */
export function formatTimestamp(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const two = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

export interface SourceLocatorNumeric {
  /** i18n key suffix under chat.sources.locator.kind (page/slide/sheet/line). */
  kindKey: string;
  /** Number of distinct positions, used to pick singular vs plural in i18n. */
  count: number;
  /** Compact position string, e.g. "1–2" or "1–2, 5". */
  positions: string;
}

export interface SourceLocatorParts {
  /** Localized page/slide/sheet label parts, when the format is paginated. */
  numeric?: SourceLocatorNumeric;
  /**
   * Chapter/section/subsection heading chain, verbatim from the document.
   * Present whenever the chunk carried a heading (all formats).
   */
  section?: string;
}

/**
 * Collapse a sorted unique number list into a compact range string using an
 * en-dash for ranges. [1,2,3] -> "1–3"; [1,2,5] -> "1–2, 5"; [4] -> "4".
 */
export function formatPositions(positions: number[]): string {
  if (positions.length === 0) {
    return '';
  }
  const sorted = [...positions].sort((a, b) => a - b);
  const parts: string[] = [];
  let runStart = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const cur = sorted[i];
    if (i < sorted.length && cur === prev + 1) {
      prev = cur;
      continue;
    }
    parts.push(runStart === prev ? `${runStart}` : `${runStart}–${prev}`);
    if (i < sorted.length) {
      runStart = cur;
      prev = cur;
    }
  }
  return parts.join(', ');
}

/**
 * Build the location display parts for a source locator, or null when there is
 * nothing to show (no page number and no heading).
 */
export function formatSourceLocator(
  locator: SourceLocator | null | undefined
): SourceLocatorParts | null {
  if (!locator) {
    return null;
  }

  const section = locator.section_path?.trim() || undefined;

  // A chunk may carry a section heading without a loc_kind (e.g. PPTX slide
  // titles that Docling exposes as section_path but with no slide number). The
  // heading is still a usable, meaningful location, so surface it.
  if (!locator.loc_kind) {
    return section ? { section } : null;
  }

  // XLSX: when we have the real worksheet name it fully identifies the
  // location, so show name (+ cited cell range when known) and suppress the
  // redundant numeric "Sheet N" index. Without a name, fall through to
  // "Sheet N".
  const sheetName = locator.sheet_name?.trim() || (locator.loc_kind === 'sheet' ? section : undefined);
  if (locator.loc_kind === 'sheet' && sheetName) {
    const range = locator.a1_range?.trim();
    return { section: range ? `${sheetName} · ${range}` : sheetName };
  }

  // Transcripts: the cited segment's time range is the location.
  if (locator.loc_kind === 'timestamp' && typeof locator.t_start === 'number') {
    const start = formatTimestamp(locator.t_start);
    const end = typeof locator.t_end === 'number' && locator.t_end > locator.t_start
      ? `–${formatTimestamp(locator.t_end)}`
      : '';
    const time = `${start}${end}`;
    return { section: section ? `${time} · ${section}` : time };
  }

  let numeric: SourceLocatorNumeric | undefined;
  const kindKey = KIND_KEY[locator.loc_kind];
  if (kindKey) {
    const numbers = (locator.positions ?? []).filter(
      (n): n is number => typeof n === 'number' && Number.isFinite(n)
    );
    if (numbers.length > 0) {
      numeric = { kindKey, count: numbers.length, positions: formatPositions(numbers) };
    }
  }

  if (!numeric && !section) {
    return null;
  }
  return { numeric, section };
}

/**
 * Build a URL fragment that jumps the in-browser viewer to the cited location,
 * or '' when no reliable jump exists for this locator.
 *
 * Only PDFs support deep-linking: the browser PDF viewer honors the
 * standardized `#page=N` fragment (PDF Open Parameters). We jump to the FIRST
 * cited page. Slides/sheets/sections have no cross-viewer fragment standard
 * (PPTX/XLSX/DOCX typically download rather than render), so we return '' and
 * the document simply opens at the start — best-effort, never a broken jump.
 */
export function buildLocatorFragment(
  locator: SourceLocator | null | undefined
): string {
  if (locator?.loc_kind !== 'page') {
    return '';
  }
  const valid = (locator.positions ?? []).filter(
    (n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0
  );
  if (valid.length === 0) {
    return '';
  }
  // Jump to the lowest cited page (defensive: do not assume input is sorted).
  return `#page=${Math.min(...valid)}`;
}

/**
 * Build a URL Text Fragment (`#:~:text=...`) that highlights the cited passage
 * in an HTML/web source, or '' when no reliable fragment can be made.
 *
 * Text Fragments (https://wicg.github.io/scroll-to-text-fragment/) let the
 * browser scroll to and highlight an exact text snippet on a live page — turning
 * a "opens the website at the top" citation into "opens AND highlights the cited
 * sentence". This is best-effort by design: if the live page text drifts from
 * our stored excerpt (edits, dynamic rendering, normalization), the browser
 * simply ignores the fragment and opens the page normally — never a broken jump.
 *
 * We use the START,END form (first/last few words) rather than the full excerpt:
 * it is far more robust to minor whitespace/punctuation differences than an exact
 * full-string match, and keeps the URL short. We deliberately do NOT emit a
 * fragment for very short excerpts (too ambiguous — could match the wrong place).
 *
 * @param excerpt The cited passage text (from the chunk).
 * @returns A `#:~:text=...` fragment, or '' when not applicable.
 */
export function buildWebTextFragment(excerpt: string | null | undefined): string {
  // Strip the excerpt-truncation ellipsis: it is our artifact, never page text,
  // and would make the END anchor unmatchable.
  const text = (excerpt ?? '')
    .replace(/…|\.\.\./g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) {
    return '';
  }
  const words = text.split(' ');
  // Too short to anchor reliably: skip rather than risk a wrong-place highlight.
  if (words.length < 4) {
    return '';
  }
  // For a short-enough excerpt, match it whole; otherwise use START,END anchors
  // (first 5 + last 5 words) which survive minor mid-passage differences.
  if (words.length <= 12) {
    return `#:~:text=${encodeFragmentPart(text)}`;
  }
  const start = words.slice(0, 5).join(' ');
  const end = words.slice(-5).join(' ');
  return `#:~:text=${encodeFragmentPart(start)},${encodeFragmentPart(end)}`;
}

/**
 * Percent-encode one text-fragment component. `-`, `&` and `,` are syntax
 * characters of the `#:~:text=` directive; encodeURIComponent covers `&`/`,`
 * but leaves `-` bare, so it must be escaped manually or a hyphenated snippet
 * is parsed as a prefix-/-suffix delimiter and silently fails to match.
 */
function encodeFragmentPart(s: string): string {
  return encodeURIComponent(s).replace(/-/g, '%2D');
}
